<template>
  <div
    class="list-idea"
    :class="{ pro: idea.pro, bought: idea.pro && idea.bought }"
    @dblclick="showingId = !showingId"
  >
    <div class="list-idea__period f-between">
      <p class="time d-inline-block">
        {{ idea.period }} {{ showingId ? `id: ${idea.id}` : '' }}
      </p>
      <div v-if="idea.socialCredits" class="social d-inline-flex">
        <div class="arrow"><i-arrow /></div>
        <div
          class="points font-weight-bold"
          :class="getPriceChangeClass(idea.socialCredits)"
        >
          {{ idea.socialCredits }}
        </div>
        <div class="arrow up"><i-arrow /></div>
      </div>
    </div>
    <div :class="hiddenNumberClass" class="list-idea__title">
      <h5 class="name">
        {{
          typeof titleName === 'string' ? titleName : titleName[$i18n.locale]
        }}
      </h5>
      <h6 class="tickers">
        {{ titleTickers }}
      </h6>
    </div>
    <div class="list-idea__forecast">
      <div class="f-between">
        <p class="description">{{ $t('ideas.list.forecast.profit') }}:</p>
        <p class="number" :class="getPriceChangeClass(profitForecast)">
          {{ profitForecast | percentWithPlus(1) }}
        </p>
      </div>
      <div class="f-between">
        <p class="description">
          {{
            isShort ? $t('ideas.list.profit.down') : $t('ideas.list.profit.up')
          }}:
        </p>
        <p
          class="number"
          :class="[getPriceChangeClass(upsidePercent), hiddenNumberClass]"
          v-if="!(currentProfit > 0 && upsidePercent < 0)"
        >
          {{ upsidePercent | percentWithPlus(1) }}
        </p>
        <p class="description font-weight-bold" v-else>
          {{ $t('ideas.list.profit.overfulfill') }}
        </p>
      </div>
      <div class="f-between">
        <p class="description">
          {{ toLocaleDate(idea.openDate) }}
          {{ $t('ideas.list.forecast.open') }}:
        </p>
        <p class="number font-weight-bold" :class="hiddenNumberClass">
          {{ openPrice }} {{ idea.currency }}
        </p>
      </div>
      <div class="f-between">
        <p class="description">
          {{ toLocaleDate(idea.targetDate) }}
          {{ $t('ideas.list.forecast.close') }}:
        </p>
        <p class="number font-weight-bold" :class="hiddenNumberClass">
          {{ targetPrice }} {{ idea.currency }}
        </p>
      </div>
    </div>
    <div
      v-if="currentPrice || (idea.pro && !idea.bought)"
      class="list-idea__current"
    >
      <div class="f-between">
        <p class="description">
          {{
            (isClosed
              ? $t('ideas.list.current.price.closed')
              : $t('ideas.list.current.price.open')) + ':'
          }}
        </p>
        <p class="number" :class="hiddenNumberClass">
          {{ isClosed ? closePrice : currentPrice }} {{ idea.currency }}
        </p>
      </div>
      <div class="f-between">
        <p class="description">{{ $t('ideas.list.current.profit') }}:</p>
        <p
          class="number"
          :class="getPriceChangeClass(isClosed ? closeProfit : currentProfit)"
        >
          {{ isClosed ? closeProfit : currentProfit | percentWithPlus(1) }}
        </p>
      </div>
    </div>
    <div class="list-idea__analyst">
      <h5 class="name">{{ idea.analyst.name[$i18n.locale] }}</h5>
      <!-- TODO -->
      <!-- <div class="params">
        <p class="param">
          {{ $t('ideas.list.analyst.trust') }}
          {{ idea.analyst.socialCredits }}
        </p>
        <p class="param">
          {{ $t('ideas.list.analyst.accuracy') + ' ' + idea.analyst.accuracy }}%
        </p>
      </div> -->
    </div>
    <a
      v-if="link"
      :href="link"
      target="_blank"
      rel="noreferrer"
      class="list-idea__button"
    >
      {{
        !idea.pro
          ? $t('ideas.list.button.analyst')
          : idea.bought
          ? $t('ideas.list.button.bought')
          : $t('ideas.list.button.buy')
      }}
    </a>
  </div>
</template>

<script>
import iarrow from '@/components/icons/arrow'
import filters from '@/utils/filters'
import calcTime from '@/utils/mixins/calcTime'
import calcPrice from '@/utils/mixins/calcPrice'

export default {
  props: {
    idea: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showingId: false,
    }
  },
  mixins: [calcTime, calcPrice],
  computed: {
    isClosed() {
      return (
        this.toTimestamp(this.idea.targetDate) - this.toTimestamp(new Date()) <
        0
      )
    },
    notPaid() {
      return this.idea.pro && !this.idea.bought
    },
    isShort() {
      return this.targetPrice < 0
    },
    hiddenNumberClass() {
      return this.notPaid ? 'blur' : ''
    },
    titleName() {
      // string for blur
      if (this.notPaid) return this.$t('ideas.list.title.name')
      if (this.idea.positions.length === 1) {
        return this.idea.positions[0].companyName
      } else {
        return this.$t('ideas.list.title.name')
      }
    },
    titleTickers() {
      // string for blur
      if (this.notPaid) return 'X акций в разных долях'
      if (this.idea.positions.length === 1) {
        return this.idea.positions[0].ticker
      } else {
        return this.$tc(
          `ideas.list.title.tickers`,
          this.idea.positions.length,
          {
            count: this.idea.positions.length,
          }
        )
      }
    },
    openPrice() {
      return this.getPrice(this.idea.positions, 'openPrice')
    },
    targetPrice() {
      return this.getPrice(this.idea.positions, 'targetPrice')
    },
    closePrice() {
      return this.getPrice(this.idea.positions, 'closePrice')
    },
    profitForecast() {
      return this.notPaid
        ? this.idea.targetProfit
        : this.calcProfit(this.openPrice, this.targetPrice)
    },
    currentPrice() {
      return this.getPrice(this.idea.positions, 'currentPrice')
    },
    currentProfit() {
      return this.notPaid
        ? this.idea.currentProfit
        : this.calcProfit(this.openPrice, this.currentPrice)
    },
    closeProfit() {
      return this.notPaid
        ? this.idea.currentProfit
        : this.calcProfit(this.openPrice, this.closePrice)
    },
    upsidePercent() {
      return Math.round(this.profitForecast - this.currentProfit)
    },
    link() {
      if (this.notPaid) return 'https://t.me/RDVPREMIUMbot'
      if (this.idea.bought) return this.$route.path + '/' + this.idea.id
      return this.idea.link ? this.idea.link : false
    },
  },
  methods: {
    // forecast and current
    getPrice(positions, param) {
      // string for blur
      if (this.notPaid) {
        return '0000'
      }
      if (!positions || !param) {
        return '-'
      }
      const sumOfPostitons = positions.reduce((prevTicker, ticker) => {
        return prevTicker + ticker[param] * ticker.count
      }, 0)
      return sumOfPostitons > 100
        ? Math.round(sumOfPostitons)
        : +sumOfPostitons.toFixed(5)
    },
    calcProfit(openPrice, targetPrice) {
      return (
        Math.round((targetPrice / openPrice) * 100 - 100) *
        (openPrice < 0 && targetPrice < 0 ? -1 : 1)
      )
    },
  },
  filters: {
    percentWithPlus: filters.percentWithPlus,
  },
  components: {
    'i-arrow': iarrow,
  },
}
</script>

<style lang="scss">
.list-idea {
  padding: 20px 32px 32px;
  border: 1px solid #eaeaf5;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  color: $title-color;
  align-self: flex-start;

  &.pro::before {
    content: 'PRO';
    position: absolute;
    top: -1px;
    width: 36px;
    height: 16px;
    padding: 2px 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #000000;
    border-radius: 0px 0px 4px 4px;
    color: white;
    font-size: 10px;
    font-weight: bold;
  }
  &.bought {
    border-color: $title-color;
  }

  p {
    margin: 0;
  }
  .f-between {
    margin-bottom: 6px;
  }
  .description {
    font-size: 12px;
    line-height: 22px;
    color: #9d9dbc;
  }
  .number {
    font-size: 16px;
    line-height: 18px;
    padding-bottom: 4px;
  }
  .blur {
    filter: blur(8px);
  }

  &__period {
    box-shadow: 0px 1px 0px #e0e0ea;
    font-size: 12px;
    color: #000000;
    padding-bottom: 4px;
    .time {
      margin-top: 2px;
      word-spacing: -0.7px;
    }
    .social {
      gap: 6px;
    }
  }
  &__title {
    margin-top: -4px;

    .name,
    .tickers {
      font-size: 16px;
      line-height: 24px;
      margin-bottom: 0;
    }
    .tickers {
      font-weight: normal;
    }
  }
  &__forecast {
    margin-top: 4px;
    padding-bottom: 10px;
    box-shadow: 0px 1px 0px #e0e0ea;
  }
  &__current {
    margin-top: 2px;
  }
  &__analyst {
    margin-top: -6px;
    background: #f6f6fa;
    border-radius: 4px;
    padding: 8px 12px;
    .name,
    .params .param {
      font-size: 12px;
      line-height: 16px;
      color: #9d9dbc;
      margin-bottom: 0;
    }
    .params {
      margin-top: -2px;
      .param {
        display: inline-block;
        margin-right: 20px;
      }
    }
  }
  &__button {
    padding: 0;
    margin-top: 2px;
    border: 1px solid $primary-color2;
    border-radius: 4px;
    background: inherit;
    font-size: 12px;
    line-height: 22px;
    text-align: center;
    color: $primary-color2;
    transition: all 0.3s ease-in-out;
    &:hover {
      background: $primary-color2;
      color: #ffffff;
    }
  }
}

#app[data-theme='dark'] {
  .list-idea {
    border-color: #333333;

    &.pro::before {
      background: #d6d6d6;
      color: $title-color;
    }

    &__period,
    &__title .name,
    &__title .tickers,
    .number:not(.price_up):not(.price_down) {
      color: #d6d6d6 !important;
    }
    &__period,
    &__forecast {
      box-shadow: 0px 1px 0px #252525;
    }
    &__analyst {
      background: #222222;
      .name {
        color: #9d9dbc !important;
      }
    }
  }
}
</style>
